/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Gap 3: editar la nota de una línea del carrito después de agregada.
 * Modal simple con un TextInput multilinea. Vacío → null (sin nota).
 */
import React, { useEffect, useState } from 'react';
import { Text, TextInput, TouchableOpacity, View } from 'react-native';
import { styles } from '../posShared.styles';
import { ModalShell } from '../../../shared/modals/ModalShell';
import { ModalFormField } from '../../../shared/modals/ModalFormField';
import { modalExperience } from '../../../shared/modals/modalExperience.styles';
import { colors } from '../../../shared/theme/colors';
import { useHasBusinessCapability } from '../../../shell/session/hooks/useEffectiveBusinessCapabilities';

type PosCartLineNoteModalProps = {
  visible: boolean;
  lineId: string | null;
  itemName: string;
  currentNote: string | null;
  onClose: () => void;
  onSave: (lineId: string, note: string | null) => void;
};

const MAX_NOTE_LENGTH = 140;

export function PosCartLineNoteModal({
  visible,
  lineId,
  itemName,
  currentNote,
  onClose,
  onSave,
}: PosCartLineNoteModalProps) {
  const hasKitchen = useHasBusinessCapability('kitchen_tickets');
  const noteTitle = hasKitchen ? 'Nota para cocina' : 'Nota de la línea';
  const [draft, setDraft] = useState('');

  useEffect(() => {
    if (visible) {
      setDraft(currentNote ?? '');
    }
  }, [visible, currentNote]);

  const handleSave = () => {
    if (!lineId) return;
    const trimmed = draft.trim();
    onSave(lineId, trimmed.length > 0 ? trimmed : null);
    onClose();
  };

  const handleClear = () => {
    if (!lineId) return;
    onSave(lineId, null);
    onClose();
  };

  return (
    <ModalShell
      visible={visible}
      onClose={onClose}
      onRequestClose={onClose}
      title={noteTitle}
      subtitle={itemName}
      closeAccessibilityLabel={`Cerrar ${noteTitle.toLowerCase()}`}
      scrollable={false}
      footer={
        <View style={[styles.modalActions, modalExperience.footerActions]}>
          {currentNote ? (
            <TouchableOpacity
              style={[styles.secondaryButton, { flex: 1 }]}
              onPress={handleClear}
              accessibilityRole="button"
              accessibilityLabel="Quitar nota"
              testID="pos.cart.line.note.clear"
            >
              <Text style={[styles.secondaryButtonText, { color: colors.danger }]}>
                Quitar nota
              </Text>
            </TouchableOpacity>
          ) : null}
          <TouchableOpacity
            style={[styles.secondaryButton, { flex: 1 }]}
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel="Cancelar nota"
          >
            <Text style={styles.secondaryButtonText}>Cancelar</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.posPrimaryCta, { flex: 1 }]}
            onPress={handleSave}
            accessibilityRole="button"
            accessibilityLabel="Guardar nota"
            testID="pos.cart.line.note.save"
          >
            <Text style={styles.posPrimaryCtaText}>Guardar</Text>
          </TouchableOpacity>
        </View>
      }
    >
      <View style={modalExperience.formPanel}>
        <ModalFormField label={hasKitchen ? 'Nota para cocina' : 'Nota'}>
          <TextInput
            value={draft}
            onChangeText={setDraft}
            placeholder="Ej: sin cebolla, para llevar, menos sal"
            placeholderTextColor={colors.textMuted}
            style={[modalExperience.formInput, { minHeight: 80 }]}
            multiline
            maxLength={MAX_NOTE_LENGTH}
            returnKeyType="done"
            blurOnSubmit
            accessibilityLabel={noteTitle}
            testID="pos.cart.line.note.input"
          />
          <Text style={{ color: colors.textMuted, fontSize: 12, marginTop: 4 }}>
            {draft.length}/{MAX_NOTE_LENGTH}
          </Text>
        </ModalFormField>
      </View>
    </ModalShell>
  );
}
